import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Form } from 'react-bootstrap';
import { FiArrowLeft, FiMessageSquare } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { createSmsManagementRequest } from '../../store/actions/smsManagementActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import SmsManagementForm from '../../components/Admin/SmsManagementForm';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminSmsCreate.css';

const AdminSmsCreate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { create: createState } = useSelector((state) => state.smsManagement);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnerId, setPartnerId] = useState('');
    const [partnerError, setPartnerError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        const fetchPartners = async () => {
            try {
                setPartnersLoading(true);
                const response = await getAllPartners();
                let partnersList = [];
                if (response?.users && Array.isArray(response.users)) {
                    partnersList = response.users;
                } else if (Array.isArray(response)) {
                    partnersList = response;
                } else if (response?.partners && Array.isArray(response.partners)) {
                    partnersList = response.partners;
                } else if (response?.data && Array.isArray(response.data)) {
                    partnersList = response.data;
                }
                setPartners(partnersList);
            } catch (error) {
                setPartnerError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setPartnersLoading(false);
            }
        };
        fetchPartners();
    }, []);

    useEffect(() => {
        if (createState.success) {
            setSuccessMessage('SMS management créé avec succès !');
            const timer = setTimeout(() => navigate('/admin/sms'), 1500);
            return () => clearTimeout(timer);
        }
    }, [createState.success, navigate]);

    const handleSubmit = (payload) => {
        if (!partnerId) {
            setPartnerError('Veuillez sélectionner un partenaire');
            return;
        }
        setPartnerError(null);
        dispatch(createSmsManagementRequest(partnerId, payload));
    };

    const partnerField = (
        <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">Partenaire *</Form.Label>
            <Form.Select
                value={partnerId}
                onChange={(e) => {
                    setPartnerId(e.target.value);
                    setPartnerError(null);
                }}
                disabled={createState.loading || partnersLoading}
                isInvalid={!!partnerError && !partnerId}
            >
                <option value="">Sélectionner un partenaire…</option>
                {partners.map((partner) => {
                    const id = partner.user_uuid || partner.partner_id || partner.id;
                    const name = partner.company_name
                        || `${partner.first_name || ''} ${partner.last_name || ''}`.trim()
                        || partner.email
                        || 'N/A';
                    return (
                        <option key={id} value={id}>
                            {name}
                        </option>
                    );
                })}
            </Form.Select>
            {partnerError && (
                <Form.Text className="text-danger">{partnerError}</Form.Text>
            )}
        </Form.Group>
    );

    return (
        <div className="dashboard-page admin-sms-create-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiMessageSquare className="me-2" />
                    Créer un SMS Management
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            {successMessage && (
                <Alert variant="success" className="mb-3">{successMessage}</Alert>
            )}

            <Card>
                <Card.Body>
                    {partnersLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" size="sm" />
                            <span className="ms-2 text-muted">Chargement des partenaires…</span>
                        </div>
                    ) : (
                        <SmsManagementForm
                            partnerField={partnerField}
                            onSubmit={handleSubmit}
                            onCancel={() => navigate(-1)}
                            loading={createState.loading}
                            externalError={createState.error}
                            submitLabel={createState.loading ? 'Création…' : 'Créer'}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminSmsCreate;
